import React, { useCallback, useEffect, useMemo, useRef, useState, type FC } from "react";
import clsx from "clsx";
import { StatusDot, type StatusDotColor } from "@/components/ui/status-dot";
import type { TaskStep } from "@/services/api/data-hub/types";
import { groupTaskSteps } from "@/components/ui/task-steps-progress/groupTaskSteps";
import {
  TASK_CANCELLED_YELLOW,
  TASK_COUNTER_DARK,
  TASK_COUNTER_LIGHT,
  TASK_LIGHT_RED,
} from "@/components/ui/task-steps-progress/taskProgressTokens";
/** Truncate a string in the middle, keeping the start and end visible. */
function middleTruncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  const keep = Math.floor((maxLength - 1) / 2);
  return `${text.slice(0, keep)}\u2026${text.slice(text.length - keep)}`;
}

const STATUS_DOT: Record<TaskStep["status"], { color: StatusDotColor; pulse: boolean }> = {
  done: { color: "emerald", pulse: false },
  running: { color: "indigo", pulse: true },
  stalled: { color: "amber", pulse: true },
  pending: { color: "gray", pulse: false },
  failed: { color: "red", pulse: false },
};

const STATUS_TEXT_CLASS: Record<TaskStep["status"], string> = {
  done: "text-slate-500 dark:text-slate-400",
  running: "text-slate-900 dark:text-slate-100 font-medium",
  stalled: "text-amber-600 dark:text-amber-400 font-medium",
  pending: "text-slate-400 dark:text-slate-500",
  failed: "text-red-600 dark:text-red-400",
};

function isIncompleteStep(step: TaskStep): boolean {
  return step.status !== "done";
}

function cancelledIncompleteDot(step: TaskStep): StatusDotColor {
  if (step.status === "pending") return "yellowMuted";
  if (step.status === "failed") return "red";
  return "yellow";
}

function cancelledIncompleteTextClass(step: TaskStep): string {
  if (step.status === "pending" || step.status === "running" || step.status === "stalled") {
    return STATUS_TEXT_CLASS.pending;
  }
  return STATUS_TEXT_CLASS[step.status] ?? STATUS_TEXT_CLASS.pending;
}

function stepPresentation(step: TaskStep, highlightIncomplete: boolean): { dot: { color: StatusDotColor; pulse: boolean }; textClass: string } {
  if (highlightIncomplete && isIncompleteStep(step) && step.status !== "failed") {
    return {
      dot: { color: cancelledIncompleteDot(step), pulse: false },
      textClass: cancelledIncompleteTextClass(step),
    };
  }
  return {
    dot: STATUS_DOT[step.status] ?? STATUS_DOT.pending,
    textClass: STATUS_TEXT_CLASS[step.status] ?? STATUS_TEXT_CLASS.pending,
  };
}

function cancelledGroupDotColor(steps: TaskStep[], incompleteCount: number): StatusDotColor | null {
  if (incompleteCount === 0) return null;
  const hasActiveIncomplete = steps.some(
    (s) => s.status !== "done" && s.status !== "pending" && s.status !== "failed",
  );
  const hasFailedOnly =
    !hasActiveIncomplete && steps.some((s) => s.status === "failed") && !steps.some((s) => s.status === "pending");
  if (hasFailedOnly) return "red";
  return hasActiveIncomplete ? "yellow" : "yellowMuted";
}

export interface TaskStepListProps {
  steps: TaskStep[];
  /** Scroll the active running row into view when steps change. */
  runningRef?: React.RefObject<HTMLLIElement | null>;
  /** When true, pending/running/failed steps use amber styling (e.g. task cancelled). */
  highlightIncomplete?: boolean;
  className?: string;
  listClassName?: string;
  /** When true, an expanded item group list fills remaining vertical space (scroll inside the list only). */
  expandableListFillsSpace?: boolean;
  /** Fired when any item group is expanded or all are collapsed. */
  onAnyItemGroupExpandedChange?: (expanded: boolean) => void;
}

/** Renders task steps with expandable item groups (same layout as Task Manager detail). */
export const TaskStepList: FC<TaskStepListProps> = ({
  steps,
  runningRef,
  highlightIncomplete = false,
  className,
  listClassName = "space-y-2",
  expandableListFillsSpace = false,
  onAnyItemGroupExpandedChange,
}) => {
  const openGroupCountRef = useRef(0);

  useEffect(() => {
    openGroupCountRef.current = 0;
    onAnyItemGroupExpandedChange?.(false);
  }, [steps, onAnyItemGroupExpandedChange]);

  const handleGroupOpenChange = useCallback(
    (open: boolean) => {
      openGroupCountRef.current = Math.max(0, openGroupCountRef.current + (open ? 1 : -1));
      onAnyItemGroupExpandedChange?.(openGroupCountRef.current > 0);
    },
    [onAnyItemGroupExpandedChange],
  );

  const resolvedClassName =
    className ??
    (expandableListFillsSpace
      ? "flex min-h-0 flex-1 flex-col overflow-hidden space-y-2"
      : "space-y-2 max-h-64 overflow-y-auto scrollbar-hide");

  if (steps.length === 0) return null;

  return (
    <ul className={`${resolvedClassName} ${listClassName}`} data-testid="task-step-list">
      {groupTaskSteps(steps).map((block) => {
          if (block.kind === "item-group") {
            return (
              <ItemGroupRow
                key={`g-${block.startIndex}`}
                steps={block.steps}
                title={block.title}
                phaseStep={block.phaseStep}
                runningRef={runningRef}
                highlightIncomplete={highlightIncomplete}
                expandableListFillsSpace={expandableListFillsSpace}
                onOpenChange={handleGroupOpenChange}
              />
            );
          }
          const step = block.step;
          const { dot, textClass } = stepPresentation(step, highlightIncomplete);
          return (
            <li
              key={block.index}
              ref={step.status === "running" || step.status === "stalled" ? runningRef : undefined}
              className="flex items-center gap-2.5 min-w-0"
            >
              <StatusDot color={dot.color} pulse={dot.pulse} size="sm" />
              <span className={`text-sm whitespace-nowrap shrink-0 ${textClass}`}>{step.label}</span>
              {step.detail && (
                <span
                  className="ml-auto text-xs text-slate-400 dark:text-slate-500 tabular-nums whitespace-nowrap overflow-hidden min-w-0"
                  title={step.detail}
                >
                  {middleTruncate(step.detail, 64)}
                </span>
              )}
            </li>
          );
        })}
    </ul>
  );
};

interface ItemGroupRowProps {
  steps: TaskStep[];
  title?: string;
  phaseStep?: TaskStep;
  runningRef?: React.RefObject<HTMLLIElement | null>;
  highlightIncomplete?: boolean;
  expandableListFillsSpace?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const ItemGroupRow: FC<ItemGroupRowProps> = ({
  steps,
  title,
  phaseStep,
  runningRef,
  highlightIncomplete = false,
  expandableListFillsSpace = false,
  onOpenChange,
}) => {
  const [open, setOpen] = useState(false);
  const counts = useItemCounts(steps);
  const running = steps.find((s) => s.status === "running" || s.status === "stalled");
  const failed = steps.filter((s) => s.status === "failed");
  const hasFailures = failed.length > 0;
  const phaseRunning = phaseStep?.status === "running" || phaseStep?.status === "stalled";
  const incompleteCount = counts.pending + counts.running + counts.failed;
  const cancelledDot = highlightIncomplete ? cancelledGroupDotColor(steps, incompleteCount) : null;

  const dotColor: StatusDotColor = cancelledDot ??
    (running || phaseRunning
      ? "indigo"
      : hasFailures
        ? counts.done > 0
          ? "amber"
          : "red"
        : counts.pending === 0
          ? "emerald"
          : "gray");
  const dotPulse = !!(running || phaseRunning) && !highlightIncomplete;
  const headerLabel = title ?? "Processed items";

  const handleToggle = () => {
    const next = !open;
    setOpen(next);
    onOpenChange?.(next);
  };

  return (
    <li
      ref={running || phaseRunning ? runningRef : undefined}
      className={clsx("min-w-0", open && expandableListFillsSpace && "flex min-h-0 flex-1 flex-col")}
    >
      <button
        type="button"
        onClick={handleToggle}
        className="w-full flex shrink-0 items-center gap-2.5 min-w-0 text-left hover:bg-slate-50 dark:hover:bg-slate-700/30 rounded px-1 -mx-1 py-0.5"
        data-testid="task-step-item-group-toggle"
      >
        <StatusDot color={dotColor} pulse={dotPulse} size="sm" />
        <span className="text-sm font-medium truncate text-slate-700 dark:text-slate-200" title={headerLabel}>
          {headerLabel}
        </span>
        {running?.detail && !open && (
          <span className="text-xs text-slate-400 dark:text-slate-500 truncate min-w-0" title={running.detail}>
            {middleTruncate(running.detail, 48)}
          </span>
        )}
        <ItemGroupCounter
          total={steps.length}
          done={counts.done}
          failureCount={failed.length}
          inProgress={!highlightIncomplete && (counts.pending + counts.running > 0 || !!phaseRunning)}
          highlightIncomplete={highlightIncomplete}
          incompleteCount={incompleteCount}
        />
      </button>
      {open && (
        <ul
          className={clsx(
            "mt-1.5 ml-3 pl-2 border-l border-slate-200 dark:border-slate-700 space-y-1 overflow-y-auto",
            expandableListFillsSpace ? "min-h-0 flex-1" : "max-h-72",
          )}
          data-testid="task-step-item-group-list"
        >
          {steps.map((step, i) => {
            const { dot, textClass } = stepPresentation(step, highlightIncomplete);
            const showDetail = step.detail && (step.status === "failed" || step.status === "running" || step.status === "stalled") && !highlightIncomplete;
            return (
              <li key={i} className="flex items-center gap-2 min-w-0">
                <StatusDot color={dot.color} pulse={false} size="sm" />
                <span className={`text-xs font-mono truncate ${textClass}`} title={step.label}>
                  {step.label}
                </span>
                {showDetail && (
                  <span
                    className={`ml-auto text-xs truncate min-w-0 ${step.status === "failed" ? "text-red-600 dark:text-red-400" : "text-slate-400 dark:text-slate-500"}`}
                    title={step.detail}
                  >
                    {middleTruncate(step.detail, 80)}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </li>
  );
};

interface ItemGroupCounterProps {
  total: number;
  done: number;
  failureCount: number;
  inProgress: boolean;
  highlightIncomplete: boolean;
  incompleteCount: number;
}

function ItemGroupCounter({
  total,
  done,
  failureCount,
  inProgress,
  highlightIncomplete,
  incompleteCount,
}: ItemGroupCounterProps) {
  if (highlightIncomplete && incompleteCount > 0) {
    return (
      <span className="ml-auto flex items-center gap-2 text-xs tabular-nums shrink-0">
        {failureCount > 0 && <span className={TASK_LIGHT_RED}>{failureCount} failed</span>}
        <span className={TASK_CANCELLED_YELLOW}>{incompleteCount} not completed</span>
        <span className={TASK_COUNTER_DARK}>/ {total}</span>
      </span>
    );
  }

  return (
    <span className="ml-auto flex items-center gap-2 text-xs tabular-nums shrink-0">
      {failureCount > 0 && <span className={TASK_LIGHT_RED}>{failureCount} failed</span>}
      {inProgress ? (
        <>
          <span className={TASK_COUNTER_LIGHT}>{done} done</span>
          <span className={TASK_COUNTER_DARK}>/ {total}</span>
        </>
      ) : (
        <span className={TASK_COUNTER_DARK}>
          {done} / {total}
        </span>
      )}
    </span>
  );
}

function useItemCounts(steps: TaskStep[]): { done: number; failed: number; pending: number; running: number } {
  return useMemo(() => {
    let done = 0,
      failed = 0,
      pending = 0,
      running = 0;
    for (const s of steps) {
      if (s.status === "done") done += 1;
      else if (s.status === "failed") failed += 1;
      else if (s.status === "running" || s.status === "stalled") running += 1;
      else pending += 1;
    }
    return { done, failed, pending, running };
  }, [steps]);
}
